import { LoginRequestModel, PasswordResetModel } from '@ecdlink/core';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  BannerWrapper,
  Button,
  Divider,
  PasswordInput,
  Typography,
} from '@ecdlink/ui';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory, useLocation } from 'react-router-dom';

import * as styles from './new-password.styles';

import {
  initialNewPasswordValues,
  NewPasswordModel,
  newPasswordSchema,
} from '@schemas/auth/password/new-password';

import { authActions, authThunkActions } from '@store/auth';
import { useAppDispatch } from '@store';
import AuthService from '@services/AuthService/AuthService';

export const NewPassword: React.FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { search } = useLocation();

  const [isLoading, setIsLoading] = useState(false);
  const [displayError, setDisplayError] = useState(false);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(true);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const {
    register,
    formState: { isValid },
    getValues,
    watch,
  } = useForm<NewPasswordModel>({
    resolver: yupResolver(newPasswordSchema),
    defaultValues: initialNewPasswordValues,
    mode: 'onChange',
  });

  const passwordValue = watch('password');

  const submitForm = async () => {
    setHasSubmitted(true);
    setDisplayError(false);

    if (!isValid) return;

    setIsLoading(true);

    // Reset authentication state
    dispatch(authActions.resetAuthState());

    const params = new URLSearchParams(search);
    const resetToken = params.get('token') ?? '';
    const username = params.get('username') ?? '';

    const body: PasswordResetModel = {
      username,
      password: getValues().password,
      resetToken,
    };

    const response = await new AuthService().SendNewPasswordRequest(body);

    if (response.valid) {
      const loginBody: LoginRequestModel = {
        username,
        password: getValues().password,
      };

      try {
        const isAuthenticated = await dispatch(
          authThunkActions.login(loginBody)
        ).unwrap();

        if (isAuthenticated) {
          history.push('/');
        } else {
          setDisplayError(true);
        }
      } catch {
        setDisplayError(true);
      }
    } else {
      setDisplayError(true);
    }

    setIsLoading(false);
  };

  // Enable and disable button based on validation
  useEffect(() => {
    if (!hasSubmitted) {
      setSubmitButtonDisabled(!isValid);
    }
  }, [isValid, hasSubmitted]);

  return (
    <div className={styles.container}>
      <BannerWrapper
        showBackground={false}
        size="small"
        title="Reset password"
        renderBorder
        color="primary"
      >
        <div className={styles.wrapper}>
          <PasswordInput<NewPasswordModel>
            label="Enter new password that has:"
            secondaryLabel="Enter new password"
            nameProp="password"
            sufficIconColor="secondary"
            value={passwordValue}
            register={register}
            strengthMeterVisible
          />

          <Divider className="mt-6" dividerType="solid" />

          {displayError && (
            <Alert
              className="mt-8"
              title="Password reset was unsuccessful. Please contact an administrator."
              type="error"
            />
          )}

          <Button
            className={styles.formButton}
            type="filled"
            color="primary"
            disabled={submitButtonDisabled}
            isLoading={isLoading}
            onClick={submitForm}
          >
            <Typography type="help" color="white" text="Reset password" />
          </Button>
        </div>
      </BannerWrapper>
    </div>
  );
};
